import { useNamespaceSelector } from '@omaze/redux';
import React, { ChangeEvent, FormEvent, ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import type { IUseState } from '../../../shared/types/IUseState';
import {
    createResetEmailCaptureAction,
} from '../email-capture-module/actions/reset-email-capture';
import { createResetEmailCaptureWithDelayStartAction } from '../email-capture-module/actions/reset-email-capture-with-delay-start';
import { createSignUpEmailCaptureStartAction } from '../email-capture-module/actions/sign-up-email-capture-start';
import { createValidateEmailCaptureStartAction } from '../email-capture-module/actions/validate-email-capture-start';
import { ErrorType, Status } from '../email-capture-module/email-capture.state';
import { selectError } from '../email-capture-module/selectors/selectError';
import { selectStatus } from '../email-capture-module/selectors/selectStatus';

import { SignUpButton } from './SignUpButton';
import { TextInput } from './TextInput/TextInput';

export function Form (): ReactElement {
    const dispatch: Dispatch = useDispatch();
    const [email, setEmail]: IUseState<string> = useState<string>('');
    const status: Status = useNamespaceSelector(selectStatus);
    const error: ErrorType = useNamespaceSelector(selectError);
    let successMessage: string = '';
    let errorMessage: string = '';

    function handleChange (event: ChangeEvent<HTMLInputElement>): void {
        setEmail(event.target.value);
        dispatch(createResetEmailCaptureAction());
    }

    function handleSubmit (event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();
        dispatch(createValidateEmailCaptureStartAction(email));
    }

    if (status === Status.SUCCESS) {
        successMessage = 'Congratulations, you signed up!';
    } else if (status === Status.ERROR && error) {
        if (error === ErrorType.INVALID_EMAIL) {
            errorMessage = 'Email address invalid';
        } else {
            errorMessage = 'An error occurred. If the error persists, please contact us at weloveyou@omaze.com';
        }
    }

    useEffect((): void => {
        if (status === Status.INITIAL) {
            setEmail('');
        } else if (status === Status.LOADING) {
            dispatch(createSignUpEmailCaptureStartAction(email));
        } else if (status === Status.SUCCESS) {
            dispatch(createResetEmailCaptureWithDelayStartAction());
        }
    }, [status, error]);

    return (
        <form className="mt-2 mb-2 lg:mt-3 relative" onSubmit={handleSubmit} role="form">
            <TextInput
                className="pr-11"
                onChange={handleChange}
                placeholder='Enter your email address'
                label='Enter your email address'
                errorMessage={errorMessage}
                successMessage={successMessage}
                value={email}
            />
            <SignUpButton isSuccess={status === Status.SUCCESS} />
        </form>
    );
}
